import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import moment from "moment";
import {
  border, textPrimary, textSecondary, textMuted, font, success, warning, error as errorColor,
} from "../../../layouts/schoolPortal/schoolPortalTheme";
import { card } from "./recordTheme";
import { get_student_activity_api } from "../../../DAL/SchoolPortal/SchoolPortal";

/**
 * What the student has actually been doing.
 *
 * The rest of the record is built from practice answers — one slice of the
 * work. This is everything else they do, from the data the app already keeps:
 * lessons finished or left part-way (and where), asking for a simpler
 * explanation, how far through an article they read, practice sittings, mock
 * exams, and what their round reports told them. Struggling shows up here
 * first. GET /api/school/students/:id/activity.
 */

const KIND = {
  lesson:   { label: "Lesson",   color: "#7c3aed" },
  practice: { label: "Practice", color: "#1d4ed8" },
  mock:     { label: "Mock exam", color: "#b45309" },
  read:     { label: "Reading",  color: "#1f6f5c" },
  round:    { label: "Round report", color: "#c01e2e" },
};
const FILTERS = [
  { key: "all", label: "Everything" },
  { key: "flag", label: "Needs attention" },
  { key: "lesson", label: "Lessons" },
  { key: "practice", label: "Practice" },
  { key: "mock", label: "Mock exams" },
  { key: "read", label: "Reading" },
  { key: "round", label: "Round reports" },
];

const hours = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);

export default function RecordActivity({ studentId, studentName, embedded = false }) {
  const [state, setState] = useState({ loading: true, data: null });
  const [filter, setFilter] = useState("all");
  const [shown, setShown] = useState(15);
  const first = (studentName || "").split(" ")[0] || "They";

  useEffect(() => {
    let dead = false;
    get_student_activity_api(studentId, 30)
      .then((r) => { if (!dead) setState({ loading: false, data: r?.code === 200 ? r : null }); })
      .catch(() => { if (!dead) setState({ loading: false, data: null }); });
    return () => { dead = true; };
  }, [studentId]);

  const items = useMemo(() => {
    const all = state.data?.items || [];
    if (filter === "all") return all;
    if (filter === "flag") return all.filter((i) => i.flag);
    return all.filter((i) => i.kind === filter);
  }, [state.data, filter]);

  if (state.loading) {
    return <Box sx={{ ...card, maxWidth: 900, mt: 2, display: "flex", gap: 1, alignItems: "center" }}><CircularProgress size={14} /><Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary }}>Loading activity…</Typography></Box>;
  }
  const d = state.data;
  if (!d) return null;
  const s = d.summary;

  // Group the visible items by day.
  const days = [];
  for (const it of items.slice(0, shown)) {
    const key = moment(it.at).format("YYYY-MM-DD");
    if (!days.length || days[days.length - 1].key !== key) days.push({ key, items: [] });
    days[days.length - 1].items.push(it);
  }

  return (
    <Box sx={embedded ? {} : { ...card, maxWidth: 900, mt: 2 }}>
      {!embedded && (
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 1 }}>
          <Typography sx={{ fontFamily: font, fontSize: "14px", fontWeight: 800, color: textPrimary }}>What {first} has been doing</Typography>
          <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted }}>last {d.days} days</Typography>
        </Box>
      )}

      {/* The week in one line of numbers. */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 1, mb: 1.25 }}>
        {[
          { v: `${s.activeDays}/7`, l: "days active this week" },
          { v: hours(s.totalMinutes), l: "studying this week" },
          { v: s.lessonsDone, l: "lessons finished" },
          { v: s.questions ? `${s.questions} · ${Math.round((s.right / s.questions) * 100)}%` : "0", l: "questions · right" },
          { v: s.mocks, l: "mock exams" },
        ].map((x) => (
          <Box key={x.l} sx={{ border: `1px solid ${border}`, borderRadius: "8px", px: 1.25, py: 0.75 }}>
            <Typography sx={{ fontFamily: font, fontSize: "15px", fontWeight: 800, color: textPrimary, fontVariantNumeric: "tabular-nums" }}>{x.v}</Typography>
            <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: textSecondary }}>{x.l}</Typography>
          </Box>
        ))}
      </Box>

      {d.signals?.length > 0 && (
        <Box sx={{ mb: 1.25 }}>
          {d.signals.map((t) => (
            <Typography key={t} sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, lineHeight: 1.6 }}>
              <Box component="span" sx={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", backgroundColor: warning, mr: 1, verticalAlign: "middle" }} />{t}
            </Typography>
          ))}
        </Box>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
        {FILTERS.map((f) => {
          const n = f.key === "all" ? d.items.length : f.key === "flag" ? d.items.filter((i) => i.flag).length : d.items.filter((i) => i.kind === f.key).length;
          if (!n && f.key !== "all") return null;
          const on = filter === f.key;
          return (
            <Box key={f.key} onClick={() => { setFilter(f.key); setShown(15); }} sx={{
              cursor: "pointer", fontFamily: font, fontSize: "11.5px", fontWeight: 600, px: 1.25, py: 0.4, borderRadius: "999px",
              border: `1px solid ${on ? textPrimary : border}`, backgroundColor: on ? textPrimary : "transparent", color: on ? "#fff" : textSecondary,
            }}>{f.label} {n}</Box>
          );
        })}
      </Box>

      {!items.length && <Typography sx={{ fontFamily: font, fontSize: "12px", color: textMuted, py: 1 }}>Nothing here in the last {d.days} days.</Typography>}

      {days.map((day) => (
        <Box key={day.key} sx={{ mt: 1 }}>
          <Typography sx={{ fontFamily: font, fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: textMuted, mb: 0.25 }}>
            {moment(day.key).calendar(null, { sameDay: "[Today]", lastDay: "[Yesterday]", lastWeek: "dddd D MMM", sameElse: "ddd D MMM" })}
          </Typography>
          {day.items.map((it, i) => (
            <Box key={i} sx={{ display: "grid", gridTemplateColumns: "92px 1fr auto", columnGap: 1.5, py: 0.75, borderTop: `1px solid ${border}` }}>
              <Typography sx={{ fontFamily: font, fontSize: "10.5px", fontWeight: 700, color: KIND[it.kind]?.color || textSecondary, pt: "2px" }}>
                {KIND[it.kind]?.label || it.kind}
              </Typography>
              <Box>
                <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textPrimary, fontWeight: 600 }}>
                  {it.title} <Box component="span" sx={{ color: textMuted, fontWeight: 400 }}>· {it.subjectName}</Box>
                </Typography>
                {it.detail?.length > 0 && (
                  it.kind === "round"
                    ? it.detail.map((t, j) => <Typography key={j} sx={{ fontFamily: font, fontSize: "11.5px", color: textSecondary, lineHeight: 1.45, mt: 0.25 }}>“{t}”</Typography>)
                    : <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textSecondary }}>{it.detail.join(" · ")}</Typography>
                )}
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography sx={{ fontFamily: font, fontSize: "11.5px", fontWeight: 600, color: it.flag ? warning : textSecondary, whiteSpace: "nowrap" }}>
                  {it.status}
                </Typography>
                <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: textMuted }}>
                  {moment(it.at).format("HH:mm")}{it.minutes ? ` · ${it.minutes}m` : ""}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      ))}

      {items.length > shown && (
        <Typography onClick={() => setShown((n) => n + 20)} sx={{ fontFamily: font, fontSize: "12px", fontWeight: 600, color: "#c01e2e", cursor: "pointer", mt: 1 }}>
          Show more ({items.length - shown} older)
        </Typography>
      )}
    </Box>
  );
}
